(() => {
  class ImageWithHotspots extends HTMLElement {
    constructor() {
      super();
      this.activeTooltip = null;
      this.boundOnClick = this.onDocumentClick.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
      this.boundOnResize = this.debounce(() => this.drawLines(), 100);
    }

    connectedCallback() {
      this.imageWrapper = this.querySelector('.image-hotspots__image-wrapper');
      this.svg = this.querySelector('.image-hotspots__lines');
      this.hotspots = Array.from(this.querySelectorAll('.image-hotspots__hotspot'));
      this.tooltips = Array.from(this.querySelectorAll('.image-hotspots__tooltip'));
      this.layout = this.querySelector('.image-hotspots__layout');
      this.leftCards = this.querySelector('.image-hotspots__cards--left');
      this.rightCards = this.querySelector('.image-hotspots__cards--right');
      this.dots = Array.from(this.querySelectorAll('[data-hotspot-dot]'));
      this.cards = Array.from(this.querySelectorAll('.image-hotspots__card'));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => this.onDotClick(e, dot));
        dot.addEventListener('mouseenter', () => this.onDotHover(dot, true));
        dot.addEventListener('mouseleave', () => this.onDotHover(dot, false));
      });

      this.cards.forEach((card) => {
        card.addEventListener('mouseenter', () => this.onCardHover(card, true));
        card.addEventListener('mouseleave', () => this.onCardHover(card, false));
      });

      document.addEventListener('click', this.boundOnClick);
      document.addEventListener('keydown', this.boundOnKeydown);
      window.addEventListener('resize', this.boundOnResize);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.debounce(() => this.drawLines(), 50));
        this.resizeObserver.observe(this.layout);
      }

      this.drawLines();

      const images = this.querySelectorAll('img');
      images.forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => this.drawLines(), { once: true });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundOnClick);
      document.removeEventListener('keydown', this.boundOnKeydown);
      window.removeEventListener('resize', this.boundOnResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    drawLines() {
      if (!this.svg || !this.layout) return;
      this.lineMap = {};
      if (window.innerWidth < 990) {
        this.svg.innerHTML = '';
        return;
      }

      this.svg.innerHTML = '';
      const layoutRect = this.layout.getBoundingClientRect();

      this.hotspots.forEach((hotspot) => {
        const index = hotspot.dataset.hotspotIndex;
        const side = hotspot.dataset.side;
        const dot = hotspot.querySelector('[data-hotspot-dot]');
        if (!dot) return;

        const cardColumn = side === 'left' ? this.leftCards : this.rightCards;
        const card = cardColumn ? cardColumn.querySelector('[data-hotspot-index="' + index + '"]') : null;
        if (!card) return;

        const dotRect = dot.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();

        const dotX = dotRect.left + dotRect.width / 2 - layoutRect.left;
        const dotY = dotRect.top + dotRect.height / 2 - layoutRect.top;

        let cardEdgeX;
        if (side === 'left') {
          cardEdgeX = cardRect.right - layoutRect.left;
        } else {
          cardEdgeX = cardRect.left - layoutRect.left;
        }

        const hasColorScheme = Array.from(card.classList).some(function(cls) {
        return cls.startsWith('color-') && cls !== 'color-none';
        });
        const cardStyles = getComputedStyle(card);
        const lineColor = hasColorScheme ? cardStyles.backgroundColor : cardStyles.borderColor;

        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', dotX);
        line.setAttribute('y1', dotY);
        line.setAttribute('x2', cardEdgeX);
        line.setAttribute('y2', dotY);
        line.style.stroke = lineColor;
        this.svg.appendChild(line);
        this.lineMap[index] = line;
      });
    }

    onDotHover(dot, isEntering) {
      if (window.innerWidth < 990) return;
      const hotspot = dot.closest('.image-hotspots__hotspot');
      if (!hotspot) return;
      const index = hotspot.dataset.hotspotIndex;
      this.setCardActive(index, isEntering);
    }

    onCardHover(card, isEntering) {
      if (window.innerWidth < 990) return;
      const index = card.dataset.hotspotIndex;
      this.setCardActive(index, isEntering);
    }

    setCardActive(index, active) {
      this.cards.forEach((card) => {
        if (card.dataset.hotspotIndex === index) {
          if (active) {
            card.classList.add('image-hotspots__card--active');
          } else {
            card.classList.remove('image-hotspots__card--active');
          }
        }
      });

      const line = this.lineMap && this.lineMap[index];
      if (line) {
        line.classList.toggle('image-hotspots__line--active', active);
      }
    }

    onDotClick(e, dot) {
      if (window.innerWidth >= 990) return;

      e.preventDefault();
      e.stopPropagation();

      const hotspot = dot.closest('.image-hotspots__hotspot');
      const tooltip = hotspot ? hotspot.querySelector('.image-hotspots__tooltip') : null;
      if (!tooltip) return;

      if (tooltip.classList.contains('image-hotspots__tooltip--active')) {
        this.closeTooltip(tooltip);
      } else {
        this.closeAllTooltips();
        this.openTooltip(tooltip, hotspot);
      }
    }

    openTooltip(tooltip, hotspot) {
      this.positionTooltip(tooltip, hotspot);
      tooltip.classList.add('image-hotspots__tooltip--active');
      hotspot.classList.add('image-hotspots__hotspot--tooltip-open');
      this.activeTooltip = tooltip;
      this.classList.add('image-hotspots--tooltip-open');
    }

    closeTooltip(tooltip) {
      tooltip.classList.remove('image-hotspots__tooltip--active');
      tooltip.style.left = '';
      tooltip.style.transform = '';
      tooltip.style.removeProperty('--arrow-left');
      const hotspot = tooltip.closest('.image-hotspots__hotspot');
      if (hotspot) hotspot.classList.remove('image-hotspots__hotspot--tooltip-open');
      if (this.activeTooltip === tooltip) this.activeTooltip = null;
    }

    closeAllTooltips() {
      this.tooltips.forEach((t) => this.closeTooltip(t));
      this.classList.remove('image-hotspots--tooltip-open');
    }

    positionTooltip(tooltip, hotspot) {
      tooltip.classList.remove('image-hotspots__tooltip--below');
      tooltip.style.left = '';
      tooltip.style.transform = '';
      tooltip.style.removeProperty('--arrow-left');

      const image = this.querySelector('.image-hotspots__image:not([style*="display: none"])') ||
                     this.querySelector('.image-hotspots__image');
      if (!image) return;

      const imageRect = image.getBoundingClientRect();
      const hotspotRect = hotspot.getBoundingClientRect();
      const tooltipWidth = Math.min(240, window.innerWidth - 32);
      const tooltipHeight = tooltip.scrollHeight || 180;

      const spaceAbove = hotspotRect.top - imageRect.top;
      if (spaceAbove < tooltipHeight + 24) {
        tooltip.classList.add('image-hotspots__tooltip--below');
      }

      const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2;
      const viewportWidth = window.innerWidth;
      const edgePadding = 16;

      let tooltipLeft = hotspotCenterX - tooltipWidth / 2;
      let tooltipRight = hotspotCenterX + tooltipWidth / 2;

      if (tooltipLeft < edgePadding) {
        tooltipLeft = edgePadding;
      } else if (tooltipRight > viewportWidth - edgePadding) {
        tooltipLeft = viewportWidth - edgePadding - tooltipWidth;
      }

      const offsetFromCenter = tooltipLeft - (hotspotCenterX - tooltipWidth / 2);

      if (Math.abs(offsetFromCenter) > 1) {
        tooltip.style.left = '50%';
        tooltip.style.transform = 'translateX(calc(-50% + ' + offsetFromCenter + 'px))';
        const arrowPercent = ((hotspotCenterX - tooltipLeft) / tooltipWidth) * 100;
        tooltip.style.setProperty('--arrow-left', Math.max(12, Math.min(88, arrowPercent)) + '%');
      }
    }

    onDocumentClick(e) {
      if (!this.activeTooltip) return;
      if (e.target.closest('.image-hotspots__tooltip') || e.target.closest('.image-hotspots__dot')) return;
      this.closeAllTooltips();
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.activeTooltip) {
        this.closeAllTooltips();
      }
    }

    debounce(fn, delay) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  if (!customElements.get('image-with-hotspots')) {
    customElements.define('image-with-hotspots', ImageWithHotspots);
  }
})();